// Objective progress, evaluated in the reducer.
//
// It has to live here rather than in the UI because `reveal-region` is *gated*
// on it, and the rule this codebase already holds is that a command which
// fabricates an army is gated in the reducer rather than merely refused by the
// server (`next-stage`). Progress is state, it is hashed, and it travels
// through the log like everything else.
//
// Reads `position` and `industry`; writes only `run.objectives`.

import { pieceColor } from "../board";
import {
  cookingCount,
  cookingUnitAt,
  droppedAt,
  type IndustryState,
  structureAt,
  unitAt,
  unitCount,
  unitValue
} from "../industry/state";
import { valueRung } from "../materials";
import { livingSides } from "../position";
import type { GameState } from "../commands";
import type { LevelSetup, ObjectiveSetup, ObjectiveState } from "./level";

/** Objectives in the order they must be completed. */
export function inOrder(level: LevelSetup): ObjectiveSetup[] {
  return [...level.objectives].sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : 1));
}

/** The one the player is working on, or null when the level is finished. */
export function activeObjective(
  level: LevelSetup,
  progress: readonly ObjectiveState[]
): ObjectiveSetup | null {
  for (const objective of inOrder(level)) {
    const state = progress.find((entry) => entry.id === objective.id);
    if (!state?.done) return objective;
  }
  return null;
}

/**
 * How far along the active objective is, and whether it just finished.
 *
 * Only the active one is evaluated. That is what makes a level a *sequence*
 * rather than a checklist — objective 3 cannot quietly complete itself while
 * you are working on 1, and a player who wanders into a later objective's
 * square has not skipped anything.
 *
 * Progress latches: it is a high-water mark and never falls. A bar that went
 * backwards because a refinery ate two ingots would read as the game taking
 * something away.
 */
export function advanceObjectives(state: GameState): ObjectiveState[] {
  const run = state.run;
  if (!run?.level) return run?.objectives ?? [];

  const progress = run.objectives.map((entry) => ({ ...entry }));
  const objective = activeObjective(run.level, progress);
  if (!objective) return progress;

  const entry = progress.find((one) => one.id === objective.id);
  if (!entry) return progress;

  const measured = measure(state, objective);
  entry.progress = Math.max(entry.progress, measured.progress);
  if (measured.done) entry.done = true;
  return progress;
}

/**
 * Has this run been finished?
 *
 * **A level with nothing left to do is a run that has been won**, and until now
 * that was true of nothing: `activeObjective` returned null, and no caller
 * acted on it. A run could be lost — mate, stalemate, despair — and could not
 * be won. It simply kept handing you camps.
 *
 * Derived, never stamped. The objectives are already latched (`done` never goes
 * back down) and they are already in the save, so a flag beside them would be a
 * second answer to the same question, kept in step by hand across a stage
 * boundary, a reload and a replay. §12.4.9's launch is this: the last objective
 * is Diamond arriving at the pad, and completing it *is* the launch.
 *
 * A run with no level cannot be won — that is a procedural run, which ends when
 * it ends — and neither can one whose scenario has not said it is the last.
 * Every level's objectives complete eventually, and waves are what happens
 * *after* an authored scenario runs out, so "nothing left to do" cannot mean
 * "the run is over" on its own: clearing the opening tutorial would end the run
 * five minutes in. `LevelSetup.final` is the declaration.
 */
export function runIsWon(state: GameState): boolean {
  const run = state.run;
  if (!run?.level?.final) return false;
  if (run.level.objectives.length === 0) return false;
  return activeObjective(run.level, run.objectives) === null;
}

function measure(
  state: GameState,
  objective: ObjectiveSetup
): { progress: number; done: boolean } {
  const { position, industry } = state;
  const run = state.run!;

  switch (objective.kind) {
    case "defeat": {
      // The same test the stage boundary already uses: a side that cannot move
      // has been beaten whether or not its king is attacked. Latched by the
      // caller, so it stays beaten once it is — the army is swept a moment
      // later and would otherwise stop satisfying its own objective.
      const beaten =
        (state.status === "checkmate" || state.status === "stalemate") &&
        position.turn === objective.side;
      // An army with nothing left on the board counts too: a `defeat` whose
      // pieces were all taken one at a time is a `defeat`.
      //
      // **But only once it has arrived.** An army still sitting in an
      // unrevealed region has no pieces on the board either, and "nothing
      // left" is vacuously true of something that was never there — so a
      // `defeat` naming a camp behind a gate completed the instant the gate's
      // own objective did. Reported as "Level complete" with three enemies
      // standing on the board, which is exactly how it looked.
      //
      // Derived from what the level says and what has been revealed, never
      // from a "has it ever appeared" flag: a flag is a thing to keep in step
      // across a save, a replay and a stage boundary, and this is already
      // written down in two places that cannot drift.
      const gone =
        !livingSides(position).includes(objective.side ?? -1) && hasArrived(state, objective);
      return { progress: beaten || gone ? 1 : 0, done: beaten || gone };
    }

    case "deliver": {
      // There *at once*, rather than a running total of everything that ever
      // passed through. It is the reading a player can check by looking at the
      // board, and a cumulative counter would need a hook on every path
      // material can enter a square by.
      //
      // **Where** it has to arrive: your king by default, which is what every
      // level so far means, and a named square when the objective says so —
      // which is 7g's rocket pad (§12.4.9). One optional property on an
      // objective kind that already exists, rather than a foundry recipe that
      // mints something which is not a piece, so the finale is measured by
      // exactly the machinery every other level is measured by.
      //
      // Note what it deliberately does *not* do: **nothing counts queens.**
      // The objective is material arriving, however it got there, so the
      // industrial route and the sacrifice route both satisfy it. An objective
      // that counted sacrifices would forbid the route the game is about. The
      // level's *text* may say whatever it likes about eight queens; that is
      // flavour and a hint, not a rule.
      // **`deliverTo`, not merely `square`.** Every objective carries a square
      // — the loader takes it from the Tiled marker's own position — so its
      // presence is not a request. See `ObjectiveSetup.deliverTo`.
      const target =
        objective.deliverTo && objective.square !== undefined
          ? objective.square
          : position.kings[run.side];
      const grade = objective.grade ?? 0;
      const held = target >= 0 ? heldAtGrade(industry, target, grade) : 0;
      const need = objective.count ?? 1;
      return { progress: held, done: held >= need };
    }

    case "collect": {
      // **Ore this side has taken off the ground, cumulatively.** Read off the
      // stage tally, which already counts both ways material is picked up: the
      // dig pass, and a king or queen lifting what it crosses.
      //
      // Cumulative, and that is the point — see `ObjectiveKind`. It is also
      // per *front*: `next-stage` rolls the tally over, so a `collect` measures
      // the ore of the front it is on rather than of the whole run. That is the
      // reading an objective on a level wants, since the level is the front.
      const got = state.tally?.mined ?? 0;
      const want = objective.count ?? 1;
      return { progress: got, done: got >= want };
    }

    case "reach": {
      const square = objective.square ?? -1;
      const piece = square >= 0 ? position.board.get(square) : undefined;
      const there = piece !== undefined && pieceColor(piece) === run.side;
      return { progress: there ? 1 : 0, done: there };
    }
  }
}

/** A fresh progress list for a level that is just starting. */
export function openObjectives(level: LevelSetup): ObjectiveState[] {
  return inOrder(level).map((objective) => ({
    id: objective.id,
    done: false,
    progress: 0
  }));
}

/**
 * Everything at `square` of `grade` or better, however it is being held.
 *
 * Four stores, because a square can be four things and an objective measured
 * against one of them is an objective that stalls while the material is
 * plainly there:
 *
 * - **Both banks** of a piece. A king is an oven as well as a pair of hands,
 *   so ore it dug turns up in `out`; counting `in` alone made the first
 *   progress bar a player ever sees run *backwards*.
 * - **The oven.** A unit mid-cook has left the inbox and not reached the out
 *   bank, so it is in neither. Measured: a run stalled at 11 of 12 with
 *   nothing left to dig.
 * - **A building's stock**, which is where the pad's material lands if the pad
 *   is a warehouse — and a store is exactly what you would build at the end of
 *   a long lane.
 * - **The ground**, for a square with no piece and no building on it at all.
 *   An authored pad need not be either; "material arriving here" should not
 *   require the level to also decide what is standing on it.
 *
 * Artifacts are never material and never count — `unitValue` prices one at
 * nought, so a tally that took the ground at face value cannot read one crown
 * as a very good ingot.
 *
 * **The threshold is a value, not a rung** (§26 F2). An objective's `grade`
 * is a rung of the value yardstick (`valueRung`: 1, 3, 6, 12, 24), which is what
 * lets one number in a level file mean "diamond" without the format learning
 * about lines. At grade 0 it counts everything,
 * exactly as it always did.
 */
function heldAtGrade(industry: IndustryState, square: number, grade: number): number {
  const floor = valueRung(grade);
  const counts = (unit: number): boolean => unitValue(unit) >= floor;
  let total = 0;
  for (const bank of ["in", "out"] as const) {
    for (let i = 0; i < unitCount(industry, square, bank); i++) {
      if (counts(unitAt(industry, square, i, bank))) total++;
    }
  }
  for (let i = 0; i < cookingCount(industry, square); i++) {
    if (counts(cookingUnitAt(industry, square, i))) total++;
  }
  for (const unit of structureAt(industry, square)?.stock ?? []) {
    if (counts(unit)) total++;
  }
  for (const unit of droppedAt(industry, square)) {
    if (counts(unit)) total++;
  }
  return total;
}

/**
 * Has the army this objective names actually turned up?
 *
 * True when no region still to be revealed contains a piece of that side. A
 * camp waiting behind a gate is not an army you have beaten, however empty the
 * board looks — and the board *does* look empty, which is the trap.
 */
function hasArrived(state: GameState, objective: ObjectiveSetup): boolean {
  const run = state.run;
  const level = run?.level;
  if (!level) return true;
  const revealed = new Set(run!.revealed);
  return !level.regions.some(
    (region) =>
      !revealed.has(region.id) &&
      region.pieces.some(([, piece]) => pieceColor(piece) === objective.side)
  );
}
